// src/pages/scheduler/SchedulerList.tsx — grouped trigger list, summary strip,
// trigger cards + status tokens. Port of the prototype scheduler-list.jsx.

import type { CSSProperties } from "react";
import { C, F } from "../../theme";
import * as Icons from "../../components/ui/Icon";
import { Stamp, StatusChip, Toggle, iconByKey } from "./kit";
import { SCHED_ICON, TILE, formatRunAt, type SchedApp, type SchedTrigger } from "./types";

const chip = (bg: string, fg: string): CSSProperties => ({
  display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 11px", borderRadius: 999,
  background: bg, color: fg, fontFamily: F.body, fontWeight: 800, fontSize: 12.5, letterSpacing: 0.1, whiteSpace: "nowrap",
});

export function TriggerCard({ trig, onOpen, onToggle, active }: { trig: SchedTrigger; onOpen: () => void; onToggle: () => void; active: boolean }) {
  const SchedIc = iconByKey(SCHED_ICON[trig.schedule.type]);
  const external = trig.managed === "external";
  const off = !external && !trig.enabled;
  const lr = trig.lastRun;
  const status = off ? "paused" : lr ? lr.status : "never";
  return (
    <div onClick={onOpen} style={{
      background: C.card, borderRadius: 18, border: `1px solid ${active ? C.brassLt : C.hairlineLt}`,
      boxShadow: active ? C.shadowM : C.shadowS, cursor: "pointer", position: "relative", overflow: "hidden",
      opacity: off ? 0.82 : 1, transition: "box-shadow .15s, border-color .15s",
    }}>
      <div style={{ position: "absolute", top: 0, left: 18, right: 18, height: 2, background: `linear-gradient(90deg,transparent,${C.brassLt} 20%,${C.brassLt} 80%,transparent)`, opacity: off ? 0.2 : 0.5 }} />

      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px 10px", borderBottom: `1px solid ${C.hairlineLt}` }}>
        {external ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999, background: C.sageBg, border: `1px solid ${C.sageDk}33`, fontFamily: F.body, fontSize: 11.5, fontWeight: 800, color: C.sageDk, letterSpacing: 0.3 }}>
            <Icons.Cloud s={13} c={C.sageDk} /> External
          </span>
        ) : (
          <div onClick={(e) => { e.stopPropagation(); onToggle(); }} style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <Toggle on={trig.enabled} onToggle={() => { /* parent handles via wrapper click */ }} size={0.86} />
            <span style={{ fontFamily: F.body, fontSize: 12.5, fontWeight: 800, color: trig.enabled ? C.sageDk : C.inkMute, letterSpacing: 0.3, minWidth: 24 }}>{trig.enabled ? "On" : "Off"}</span>
          </div>
        )}
        <div style={{ flex: 1 }} />
        <StatusChip status={status} small />
      </div>

      <div style={{ padding: "12px 16px 14px" }}>
        <div style={{ fontFamily: F.serif, fontSize: 18, fontWeight: 700, color: C.ink, lineHeight: 1.15, letterSpacing: -0.2 }}>{trig.name}</div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 10 }}>
          <span style={chip(C.brassBg, C.brassDk)}><Icons.Lightning s={13} c={C.brassDk} />{trig.action}</span>
          <span style={chip(C.paperWarm, C.inkSoft)}><SchedIc s={13} c={C.inkSoft} />{trig.cadence}</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, paddingTop: 11, borderTop: `1px solid ${C.hairlineLt}` }}>
          <span style={{ fontSize: 11.5, color: C.inkMute, fontWeight: 700, letterSpacing: 0.2, whiteSpace: "nowrap" }}>
            {off ? "Paused" : <>Next <b style={{ color: C.inkSoft, fontFamily: F.mono, fontWeight: 700 }}>{trig.nextRun}</b></>}
          </span>
          <span style={{ flex: 1, minWidth: 8 }} />
          <span style={{ fontSize: 11.5, color: C.inkMute, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {lr ? `Last ${formatRunAt(lr.at)}` : "Never run"}
          </span>
          <Icons.Chev s={16} c={C.inkMute} />
        </div>
      </div>
    </div>
  );
}

export function GroupHeader({ app, count }: { app: SchedApp; count: number }) {
  const Ic = iconByKey(app.icon);
  const t = TILE[app.tone] || TILE.brass;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
      <div style={{ width: 42, height: 42, borderRadius: 13, background: t.bg, border: `1px solid ${C.hairlineLt}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Ic s={22} c={t.ic} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: F.serif, fontSize: 20, fontWeight: 700, color: C.ink, letterSpacing: -0.3, lineHeight: 1.1 }}>{app.name}</div>
        <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 12.5, color: C.brassDk, fontWeight: 600 }}>{app.blurb}</div>
      </div>
      <Stamp tone="paper">{count} {count === 1 ? "job" : "jobs"}</Stamp>
    </div>
  );
}

function DarkMetric({ label, value, tone = "rgba(243,220,154,0.9)" }: { label: string; value: number; tone?: string }) {
  return (
    <div style={{ flex: 1, minWidth: 90, padding: "12px 16px", borderRight: "1px dashed rgba(243,220,154,0.14)" }}>
      <div style={{ fontFamily: F.mono, fontSize: 30, fontWeight: 700, color: tone, lineHeight: 1, letterSpacing: -1 }}>{value}</div>
      <div style={{ fontSize: 10.5, color: "rgba(243,220,154,0.55)", fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", marginTop: 6 }}>{label}</div>
    </div>
  );
}

export function SummaryStrip({ triggers }: { triggers: SchedTrigger[] }) {
  const total = triggers.length;
  const active = triggers.filter((t) => t.enabled).length;
  const errors = triggers.filter((t) => t.enabled && t.lastRun?.status === "failed").length;
  return (
    <div style={{ background: "#2B2417", borderRadius: 18, border: `1px solid ${C.brassDk}`, overflow: "hidden", boxShadow: C.shadowM }}>
      <div style={{ padding: "12px 16px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px dashed rgba(243,220,154,0.22)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <Icons.Clock s={17} c={C.brassGlow} />
          <div>
            <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 11.5, color: "rgba(243,220,154,0.7)", fontWeight: 600, letterSpacing: 0.3 }}>Heartbeat scheduler</div>
            <div style={{ fontFamily: F.serif, fontSize: 16, fontWeight: 700, color: C.inkOnDark, letterSpacing: -0.2, lineHeight: 1.1 }}>All scheduled jobs</div>
          </div>
        </div>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: F.body, fontSize: 11.5, fontWeight: 800, color: "#A7C77F", letterSpacing: 0.4 }}>
          <span style={{ width: 7, height: 7, borderRadius: 999, background: "#A7C77F", boxShadow: "0 0 6px #A7C77F" }} />LIVE
        </span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", background: "rgba(255,255,255,0.02)" }}>
        <DarkMetric label="Triggers" value={total} />
        <DarkMetric label="Active" value={active} tone="#A7C77F" />
        <DarkMetric label="Errors" value={errors} tone={errors ? "#E2A79C" : "rgba(243,220,154,0.85)"} />
      </div>
    </div>
  );
}
